/**
 * Which tab this is, kept in `sessionStorage` — and whether it is really the
 * tab that storage belongs to.
 *
 * A tab keeps its holds and its unsaved form there (ADR-0003, 2026-10-22):
 * per tab, kept across a reload and across the browser discarding and
 * restoring it, gone when the tab closes. One trap: "Duplicate tab" copies
 * `sessionStorage`, so the copy would wake up believing it holds whatever the
 * original holds, and resume it out from under it.
 *
 * So a tab that finds an identity already there asks the other tabs of this
 * origin whether one of them is using it. If one answers, this is a copy: it
 * takes a fresh identity and drops what it inherited. If none does — the
 * original was reloaded, or discarded and restored — it is the same tab.
 */

const ID_KEY = 'colplat:tab';
const CHANNEL = 'colplat-tab';
/** Long enough for a busy sibling tab to answer; short enough not to delay a page anyone notices. */
const ASK_MS = 150;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

export interface TabIdentity {
  id: string;
  /** False when the storage was inherited by a duplicated tab and has been cleared. */
  inherited: boolean;
}

export interface TabIdentityOptions {
  storage?: Store | undefined;
  channel?: () => Pick<BroadcastChannel, 'postMessage' | 'addEventListener' | 'close'> | undefined;
  /** Keys a copy must not keep — the original's holds and unsaved forms. */
  inheritedPrefixes?: readonly string[];
  wait?: (ms: number) => Promise<void>;
}

function defaultStorage(): Store | undefined {
  try { return typeof sessionStorage === 'undefined' ? undefined : sessionStorage; } catch { return undefined; }
}

function defaultChannel(): BroadcastChannel | undefined {
  return typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(CHANNEL);
}

let answering: Pick<BroadcastChannel, 'postMessage' | 'addEventListener' | 'close'> | undefined;

/**
 * Settle this tab's identity, and from then on answer for it when another
 * tab asks. Call once per page, before anything reads hold tokens or saved
 * forms.
 */
export async function claimTabIdentity(opts: TabIdentityOptions = {}): Promise<TabIdentity> {
  const storage = opts.storage ?? defaultStorage();
  const prefixes = opts.inheritedPrefixes ?? ['colplat:hold:', 'colplat:form:'];
  const wait = opts.wait ?? ((ms: number) => new Promise<void>((r) => { setTimeout(r, ms); }));
  const fresh = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  let found: string | null;
  try { found = storage?.getItem(ID_KEY) ?? null; } catch { found = null; }
  const channel = (opts.channel ?? defaultChannel)();

  let inherited = false;
  if (found && channel) {
    let claimed = false;
    channel.addEventListener('message', (event: Event) => {
      const data = (event as MessageEvent).data as { t?: string; id?: string } | null;
      if (data?.t === 'mine' && data.id === found) claimed = true;
    });
    channel.postMessage({ t: 'who', id: found });
    await wait(ASK_MS);
    inherited = claimed;
  }

  let id = found;
  if (!id || inherited) {
    id = fresh();
    if (inherited && storage) {
      const doomed: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const k = storage.key(i);
        if (k && prefixes.some((p) => k.startsWith(p))) doomed.push(k);
      }
      for (const k of doomed) { try { storage.removeItem(k); } catch { /* storage off */ } }
    }
    try { storage?.setItem(ID_KEY, id); } catch { /* storage off: this page only */ }
  }

  // Answer for this identity from now on, so a later copy of this tab finds out.
  answering?.close();
  answering = channel;
  const mine = id;
  channel?.addEventListener('message', (event: Event) => {
    const data = (event as MessageEvent).data as { t?: string; id?: string } | null;
    if (data?.t === 'who' && data.id === mine) channel.postMessage({ t: 'mine', id: mine });
  });
  return { id, inherited };
}
